import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus, Pencil, Trash2, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import FileAttachments from "@/components/FileAttachments";

/**
 * Full audit drawer: tabs Details · CQC Mapping · Findings & Actions · Evidence · Closure.
 */
const TABS = [
  { key: "details",   label: "Details" },
  { key: "cqc",       label: "CQC Mapping" },
  { key: "findings",  label: "Findings & Actions" },
  { key: "evidence",  label: "Evidence" },
  { key: "closure",   label: "Closure" },
];

export default function AuditFullDrawer({ auditId, onClose, canManage, onChanged }) {
  const [tab, setTab] = useState("details");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    try { const r = await api.get(`/audits-qa/records/${auditId}`); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  if (!auditId) return null;
  const findings = data?.findings_list || [];
  const openReq = findings.filter((f) => (f.required ?? true) && (f.status || "open") !== "completed").length;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white z-50 shadow-xl border-l border-slate-200 flex flex-col" data-testid="audit-drawer">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Audit</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 truncate" data-testid="audit-drawer-title">{data?.title || "Loading…"}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5">{data?.category_label} · {data?.conducted_date || "—"} · {data?.auditor || "—"}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="btn-audit-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="border-b border-slate-200 flex gap-0 overflow-x-auto flex-nowrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`audit-drawer-tab-${t.key}`}
              className={`shrink-0 min-h-[44px] px-3 py-2 text-xs font-medium border-b-2 transition whitespace-nowrap ${tab === t.key ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading || !data ? <div className="text-sm text-slate-500">Loading…</div> : (
            <>
              {tab === "details" && <DetailsPane audit={data} />}
              {tab === "cqc" && <CqcPane audit={data} />}
              {tab === "findings" && <FindingsPane audit={data} canManage={canManage} reload={() => { load(); onChanged?.(); }} />}
              {tab === "evidence" && <div><FileAttachments module="audits" recordId={auditId} /></div>}
              {tab === "closure" && <ClosurePane audit={data} openReq={openReq} canManage={canManage} onClosed={() => { load(); onChanged?.(); }} />}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function DetailsPane({ audit }) {
  return (
    <div className="space-y-3 text-sm">
      <Row label="Score">{audit.score_num != null ? `${audit.score_num}%` : "—"}</Row>
      <Row label="Site ID">{audit.site_id || "—"}</Row>
      <Row label="Status">{(audit.status || "").replace(/_/g, " ")}</Row>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Findings summary</div>
        <div className="text-sm whitespace-pre-wrap text-slate-800">{audit.findings || "—"}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Action plan</div>
        <div className="text-sm whitespace-pre-wrap text-slate-800">{audit.action_plan || "—"}</div>
      </div>
    </div>
  );
}

function CqcPane({ audit }) {
  const five = audit.cqc_key_questions || [];
  const qs = audit.cqc_quality_statements || [];
  return (
    <div className="space-y-4">
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">CQC Five Key Questions</div>
        <div className="flex flex-wrap gap-1">
          {five.length ? five.map((k) => <span key={k} className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200">{k}</span>)
            : <span className="text-slate-400 text-sm">Not mapped</span>}
        </div>
      </section>
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Quality Statements</div>
        <div className="flex flex-wrap gap-1">
          {qs.length ? qs.map((q, i) => <span key={i} className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border bg-violet-50 text-violet-800 border-violet-200">{q}</span>)
            : <span className="text-slate-400 text-sm">Not mapped</span>}
        </div>
      </section>
      <p className="text-[11px] text-slate-500">Legacy KLOE data is preserved and shown here as CQC Five Key Questions since these frameworks map 1:1.</p>
    </div>
  );
}

function FindingsPane({ audit, canManage, reload }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultsF());
  const start = (f = null) => { setEditing(f); setForm(f ? { ...defaultsF(), ...f } : defaultsF()); setAdding(!f); };
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!form.finding?.trim()) return toast.error("Finding required");
    try {
      if (editing?.id) await api.patch(`/audits-qa/records/${audit.id}/findings/${editing.id}`, form);
      else await api.post(`/audits-qa/records/${audit.id}/findings`, form);
      toast.success(editing?.id ? "Finding updated" : "Finding added");
      setAdding(false); setEditing(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (f) => {
    if (!window.confirm("Remove this finding?")) return;
    try { await api.delete(`/audits-qa/records/${audit.id}/findings/${f.id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <div className="text-sm text-slate-700">{(audit.findings_list || []).length} finding(s), {audit.open_findings || 0} open</div>
        {canManage && <Button size="sm" onClick={() => start()} data-testid="btn-finding-new" className="h-11 min-h-[44px] bg-sky-700 hover:bg-sky-800"><Plus className="w-3.5 h-3.5 mr-1" /> Add finding</Button>}
      </div>
      <ul className="divide-y divide-slate-100">
        {(audit.findings_list || []).map((f) => (
          <li key={f.id} className="py-2" data-testid={`finding-item-${f.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm text-slate-900">{f.finding}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{f.corrective_action}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Assigned {f.assigned_to_name || "—"} · Due {f.due_date || "—"} · {f.priority} · {f.status}</div>
              </div>
              {canManage && <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => start(f)} className="p-1 text-slate-500 hover:text-sky-700"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => del(f)} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>}
            </div>
          </li>
        ))}
        {(audit.findings_list || []).length === 0 && <li className="text-sm text-slate-500 text-center py-4">No findings yet.</li>}
      </ul>
      {(adding || editing) && (
        <div className="mt-3 p-3 border border-sky-200 rounded-lg bg-sky-50" data-testid="finding-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="md:col-span-2"><FL label="Finding *"><Input value={form.finding} onChange={(e) => patch("finding", e.target.value)} data-testid="input-finding" /></FL></div>
            <div className="md:col-span-2"><FL label="Corrective action"><Textarea rows={2} value={form.corrective_action} onChange={(e) => patch("corrective_action", e.target.value)} data-testid="input-corrective" /></FL></div>
            <FL label="Assigned to"><Input value={form.assigned_to_name} onChange={(e) => patch("assigned_to_name", e.target.value)} data-testid="input-assigned" /></FL>
            <FL label="Due"><Input type="date" value={form.due_date || ""} onChange={(e) => patch("due_date", e.target.value || null)} data-testid="input-due" /></FL>
            <FL label="Priority">
              <select value={form.priority} onChange={(e) => patch("priority", e.target.value)} className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2" data-testid="input-priority">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </FL>
            <FL label="Status">
              <select value={form.status} onChange={(e) => patch("status", e.target.value)} className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2" data-testid="input-status">
                <option value="open">Open</option><option value="in_progress">In progress</option><option value="completed">Completed</option>
              </select>
            </FL>
            <div className="md:col-span-2 flex items-center gap-2 text-xs"><input type="checkbox" checked={!!form.required} onChange={(e) => patch("required", e.target.checked)} data-testid="input-required" /> <span className="text-slate-700">Required — blocks audit closure unless overridden</span></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => { setAdding(false); setEditing(null); }} data-testid="btn-finding-cancel">Cancel</Button>
            <Button size="sm" onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-finding-save">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClosurePane({ audit, openReq, canManage, onClosed }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const isClosed = audit.status === "completed";
  const close = async () => {
    if (openReq > 0 && !reason.trim()) return toast.error("Provide an override reason to close with open required actions.");
    if (!window.confirm(openReq > 0 ? "Close this audit with unresolved required actions? An override will be recorded." : "Close this audit?")) return;
    setSaving(true);
    try { await api.post(`/audits-qa/records/${audit.id}/close`, { override_reason: reason }); toast.success("Audit closed"); onClosed?.(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      {isClosed && <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">Audit closed on {audit.closed_at?.slice(0,10) || "—"} by {audit.closed_by_name || audit.closed_by || "—"}.</div>}
      {openReq > 0 && !isClosed && (
        <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
          <strong>{openReq}</strong> required corrective action(s) still open. Complete them, or provide an override reason below.
        </div>
      )}
      {!isClosed && (
        <FL label={openReq > 0 ? "Override reason *" : "Notes (optional)"}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="input-close-reason" placeholder="Why is closure appropriate despite open actions?" />
        </FL>
      )}
      {!isClosed && canManage && (
        <div className="flex justify-end">
          <Button onClick={close} disabled={saving} className="bg-red-700 hover:bg-red-800" data-testid="btn-close-audit"><Lock className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Closing…" : "Close Audit"}</Button>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (<div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className="text-sm text-slate-800 mt-0.5">{children}</div></div>);
}
function FL({ label, children }) { return <label className="block"><span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span><div className="mt-0.5">{children}</div></label>; }
function defaultsF() { return { finding: "", priority: "medium", corrective_action: "", assigned_to_name: "", due_date: null, status: "open", required: true }; }
